import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { FormControl } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { UUID } from '../../../../core/pojo/id-variable';

@Component({
  selector: 'app-checkbox-selector',
  templateUrl: './checkbox-selector.component.html',
  styleUrls: ['./checkbox-selector.component.scss'],
})
export class CheckboxSelectorComponent implements OnChanges, OnDestroy {
  private _destroyStream = new Subject<void>();

  @Input() title: string;
  @Input() items: CheckBoxSelector[] = [];
  @Input() props = new InputProps();
  @Input() isShrinked = false;
  @Input() insideModal = true;
  /**
   * Controls font size, padding, and height for each list entry in the selector dropdown.
   *
   * 🔧 HTML usage:
   * <div *ngFor="let item of dataKeys; index as i"
   *      class="list-entry {{listEntryAdditionalClass}} space-between-horizontal-centered"
   *      [class.not-in-table]="isNotInTable">
   * </div>
   *
   * 🎨 SCSS logic (located in `overlay-pop-up.component.scss`):
   *
   * .overlay-list {
   *   ...
   *   .list-block {
   *     .list-entry {
   *       ...
   *       &.not-in-table { // IMPORTANT: YOU HAVE 2 SIZING OPTIONS: your pop-up is either inside a table(smaller) or outside of it(bigger)
   *         ...
   *       }
   */
  @Input() isNotInTable = false;
  /**
   * If needed the height of the list or other properties of 'overlay-pop-up' could be changed
   * by adding a new class in the array
   * [overlayContentClass] = "['overlay-pop-up', '!max-h-[12rem]]"
   */
  @Input() overlayContentClass: string | string[] = 'overlay-pop-up';
  @Input() outerClass = 'control-container';
  @Input() customText: string; // replace 'All selected' or '2 items selected' for your custom text ex. '2 of 3 pages'
  @Input() disabled: boolean = false;
  /** see {@link OverlayPopUpComponent#blockRepositioning} */
  @Input() blockRepositioning = true;
  @Output() itemEmitter = new EventEmitter<CheckBoxSelector[]>();

  showCheckboxSelector = false;
  allValuesCheckedText: AllSelectorName;
  selectedItemsText = '';
  autocompleteControl: FormControl<string>;
  MAX_TEXT_LEN = 20;

  ngOnChanges(): void {
    this.initAutocomplete();
    this.calculateItemsChecked();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  calculateItemsChecked(): void {
    const numberOfSelectedItems = this.items?.filter((item) => item.chosen).length;
    if (numberOfSelectedItems === 1) {
      this.selectedItemsText = this.items?.filter((item) => item.chosen)[0].item;
    } else {
      this.selectedItemsText = (numberOfSelectedItems === this.items?.length ? 'All' : numberOfSelectedItems.toString()) + ' selected';
    }
    this.allValuesCheckedText = numberOfSelectedItems === this.items?.length ? 'Unselect all' : 'Select all';
  }

  changeSelection(item: CheckBoxSelector): void {
    if (item.disabled) {
      return;
    }
    item.chosen = !item.chosen;
    this.itemEmitter.emit(this.items);
    this.calculateItemsChecked();
  }

  changeAllSelected(allValuesCheckedText: AllSelectorName): void {
    if (!this.items.length) {
      return;
    }
    this.items
      .filter((item) => (!item.isHidden && !item.disabled))
      .forEach((el) => el.chosen = allValuesCheckedText === 'Select all');
    this.itemEmitter.emit(this.items);
    this.calculateItemsChecked();
  }

  private initAutocomplete(): void {
    if (!this.props.isAutocompleteEnabled) {
      return;
    }
    this.autocompleteControl = new FormControl();
    this.autocompleteControl.valueChanges
      .pipe(takeUntil(this._destroyStream))
      .subscribe((value) => {
        this.items.forEach((item) => {
          item.isHidden = !item.item.toLowerCase().includes(value.toLowerCase());
        });
      });
  }

}

type AllSelectorName = 'Unselect all' | 'Select all';

export interface CheckBoxSelector {
  itemId?: number;
  UUID?: UUID;
  item: string;
  chosen: boolean;
  isHidden?: boolean;
  disabled?: boolean;
}

export class InputProps {
  title?: string;
  titleColorClass?: string;
  required?: boolean;
  isAutocompleteEnabled?: boolean;
  isSelectAllEnabled? = true;
  maxLength? = 20;
  emptyArrayText? = 'No data';
}
